import { Link } from 'react-router-dom';

const STAGES = [
  {
    number: '01',
    id: 'identify',
    title: 'IDENTIFY',
    subtitle: 'Find Distressed Properties',
    icon: 'ri-search-eye-line',
    color: '#f59e0b',
    description: 'Use our interactive maps to locate tax-delinquent properties across California, Nevada, and Arizona before they hit the auction block.',
    bullets: [
      'Browse CA, NV & AZ foreclosure maps',
      'Filter by taxes owed, property type & county',
      'Review auction dates and minimum bids',
      'Track Active vs. Upcoming vs. Pending status',
      'Identify high-equity distressed assets early',
    ],
  },
  {
    number: '02',
    id: 'research',
    title: 'RESEARCH',
    subtitle: 'Due Diligence & Data',
    icon: 'ri-file-search-line',
    color: '#f97316',
    description: 'Dig into parcel records, tax histories, lien stacks, and comparable sales to assess true property value and hidden risks before you bid.',
    bullets: [
      'Pull parcel data from county assessor records',
      'Check for IRS liens, HOA dues & code violations',
      'Verify chain of title and ownership history',
      'Run comparable sales analysis (comps)',
      'Estimate rehab costs and after-repair value (ARV)',
    ],
  },
  {
    number: '03',
    id: 'acquire',
    title: 'ACQUIRE',
    subtitle: 'Win the Bid, Secure the Deed',
    icon: 'ri-auction-line',
    color: '#84cc16',
    description: 'Attend the county auction armed with your max bid, required deposits, and documentation. Win the certificate of sale and start the deed process.',
    bullets: [
      'Register with the county as a qualified bidder',
      'Bring certified funds or pre-approved financing',
      'Bid strategically — know your max and stick to it',
      'Receive certificate of sale or treasurer\'s deed',
      'File all required post-auction paperwork promptly',
    ],
  },
  {
    number: '04',
    id: 'recover',
    title: 'RECOVER',
    subtitle: 'Rehab, Title & Liens',
    icon: 'ri-tools-line',
    color: '#14b8a6',
    description: 'Clear any remaining liens, resolve title issues, and bring the property up to marketable condition through targeted rehabilitation work.',
    bullets: [
      'File for quiet title action if needed',
      'Negotiate and settle any subordinate liens',
      'Complete structural and cosmetic rehabilitation',
      'Bring utilities, permits & code compliance current',
      'Stage and photograph for maximum market appeal',
    ],
  },
  {
    number: '05',
    id: 'monetize',
    title: 'MONETIZE',
    subtitle: 'Sell, Rent or Hold',
    icon: 'ri-money-dollar-circle-line',
    color: '#22c55e',
    description: 'Deploy your monetization strategy — flip for immediate profit, rent for passive income, or hold long-term for appreciation and equity growth.',
    bullets: [
      'List on MLS for retail sale at full ARV',
      'Wholesale to another investor for quick profit',
      'Rent out for monthly cash flow income stream',
      'Refinance and pull out equity tax-deferred (BRRRR)',
      'Hold in portfolio for long-term appreciation',
    ],
  },
  {
    number: '06',
    id: 'reinvest',
    title: 'REINVEST',
    subtitle: 'Compound Your Returns',
    icon: 'ri-refresh-line',
    color: '#f59e0b',
    description: 'Roll your profits back into the next deal. Each cycle compounds your capital base, deal flow, and market expertise — building lasting wealth.',
    bullets: [
      'Use 1031 exchanges to defer capital gains taxes',
      'Scale into larger multifamily and commercial deals',
      'Build systems and teams for faster deal velocity',
      'Diversify across CA, NV & AZ markets',
      'Track IRR and optimize your deal criteria over time',
    ],
  },
];

const CONNECTOR_ARROWS = [0, 1, 2, 4];

export default function ForeclosureRecoveryPage() {
  return (
    <div className="min-h-screen bg-[#070c14] text-white">
      {/* Header */}
      <div className="bg-[#0d1421] border-b border-gray-800/60">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="flex items-center gap-1.5 text-gray-400 hover:text-gray-200 transition-colors text-sm cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </div>
              Back
            </Link>
            <div className="h-4 w-px bg-gray-700"></div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-route-line text-amber-400"></i>
              </div>
              <span className="text-gray-300 font-medium text-sm">Foreclosure Recovery Workflow</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/ca-tax-foreclosures"
              className="text-xs px-3 py-1.5 rounded-full border border-orange-500/30 text-orange-400 bg-orange-500/10 hover:bg-orange-500/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              CA Map
            </Link>
            <Link
              to="/nv-tax-foreclosures"
              className="text-xs px-3 py-1.5 rounded-full border border-amber-500/30 text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              NV Map
            </Link>
            <a
              href="https://realestatedevelopmentauthority.net/az-tax-sales"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs px-3 py-1.5 rounded-full border border-green-500/30 text-green-400 bg-green-500/10 hover:bg-green-500/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              AZ Sales
            </a>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-950/20 via-transparent to-emerald-950/20 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-6 py-16 text-center relative">
          <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-full px-4 py-1.5 mb-6">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-route-line text-amber-400 text-xs"></i>
            </div>
            <span className="text-amber-400 text-xs font-semibold tracking-widest uppercase">6-Stage Framework</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black mb-5 leading-tight tracking-tight">
            The Foreclosure Recovery
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-emerald-400">
              Investment Playbook
            </span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
            A proven six-stage system for acquiring tax-delinquent properties at deep discounts, recovering their value, and building generational wealth through strategic real estate investment.
          </p>

          <div className="flex items-center justify-center gap-8 mt-10">
            <div className="text-center">
              <div className="text-3xl font-black text-amber-400">6</div>
              <div className="text-gray-500 text-xs mt-1 tracking-wide uppercase">Stages</div>
            </div>
            <div className="w-px h-10 bg-gray-700"></div>
            <div className="text-center">
              <div className="text-3xl font-black text-emerald-400">3</div>
              <div className="text-gray-500 text-xs mt-1 tracking-wide uppercase">States</div>
            </div>
            <div className="w-px h-10 bg-gray-700"></div>
            <div className="text-center">
              <div className="text-3xl font-black text-orange-400">100+</div>
              <div className="text-gray-500 text-xs mt-1 tracking-wide uppercase">Active Listings</div>
            </div>
          </div>
        </div>
      </div>

      {/* Stage Cards */}
      <div className="max-w-7xl mx-auto px-6 pb-16">
        {/* Flow indicator */}
        <div className="flex items-center justify-center gap-2 mb-12">
          {STAGES.map((stage, i) => (
            <>
              <div
                key={stage.id}
                className="flex items-center gap-1.5"
              >
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black"
                  style={{ backgroundColor: `${stage.color}20`, color: stage.color, border: `1.5px solid ${stage.color}50` }}
                >
                  {i + 1}
                </div>
                <span className="text-xs font-bold tracking-wider" style={{ color: stage.color }}>{stage.title}</span>
              </div>
              {i < STAGES.length - 1 && (
                <div key={`arrow-${i}`} className="w-5 h-px bg-gray-700 mx-1"></div>
              )}
            </>
          ))}
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {STAGES.map((stage, index) => (
            <div
              key={stage.id}
              className="relative bg-[#0d1421] rounded-2xl border overflow-hidden group hover:border-opacity-60 transition-all duration-300"
              style={{ borderColor: `${stage.color}25` }}
            >
              {/* Top accent line */}
              <div className="h-0.5 w-full" style={{ background: `linear-gradient(to right, ${stage.color}, transparent)` }}></div>

              <div className="p-6">
                {/* Stage number + icon */}
                <div className="flex items-start justify-between mb-5">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${stage.color}18`, border: `1px solid ${stage.color}30` }}
                  >
                    <div className="w-7 h-7 flex items-center justify-center">
                      <i className={`${stage.icon} text-xl`} style={{ color: stage.color }}></i>
                    </div>
                  </div>
                  <div
                    className="text-4xl font-black leading-none"
                    style={{ color: `${stage.color}15` }}
                  >
                    {stage.number}
                  </div>
                </div>

                {/* Title */}
                <div className="mb-1">
                  <span
                    className="text-xs font-black tracking-widest uppercase"
                    style={{ color: stage.color }}
                  >
                    Stage {index + 1} — {stage.title}
                  </span>
                </div>
                <h3 className="text-white font-bold text-lg mb-3 leading-tight">{stage.subtitle}</h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-5">{stage.description}</p>

                {/* Bullets */}
                <ul className="space-y-2">
                  {stage.bullets.map((bullet, bi) => (
                    <li key={bi} className="flex items-start gap-2.5 text-xs text-gray-400">
                      <div className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-arrow-right-s-line text-xs" style={{ color: stage.color }}></i>
                      </div>
                      <span className="leading-relaxed">{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Bottom connector indicator */}
              {index < STAGES.length - 1 && (
                <div
                  className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full flex items-center justify-center z-10"
                  style={{ backgroundColor: '#0d1421', border: `1px solid ${stage.color}30` }}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-down-s-line text-xs" style={{ color: stage.color }}></i>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-16 relative overflow-hidden rounded-2xl border border-amber-500/20 bg-gradient-to-br from-[#0d1421] via-[#111827] to-[#0a1020] p-10 text-center">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 via-transparent to-emerald-500/5 pointer-events-none"></div>
          <div className="relative">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-amber-500/15 border border-amber-500/25 mx-auto mb-5">
              <i className="ri-map-2-fill text-amber-400 text-2xl"></i>
            </div>
            <h2 className="text-2xl font-black mb-3 text-white">Ready to Start Stage 1?</h2>
            <p className="text-gray-400 mb-8 max-w-lg mx-auto text-sm leading-relaxed">
              Begin identifying tax-delinquent properties right now on our interactive foreclosure maps. Filter by county, price range, and auction status to find your next deal.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/ca-tax-foreclosures"
                className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all cursor-pointer whitespace-nowrap bg-orange-500/15 border border-orange-500/30 text-orange-300 hover:bg-orange-500/25"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-map-pin-2-fill text-orange-400"></i>
                </div>
                CA Tax Foreclosures
              </Link>
              <Link
                to="/nv-tax-foreclosures"
                className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all cursor-pointer whitespace-nowrap bg-amber-500/15 border border-amber-500/30 text-amber-300 hover:bg-amber-500/25"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-map-pin-2-fill text-amber-400"></i>
                </div>
                NV Tax Foreclosures
              </Link>
              <a
                href="https://realestatedevelopmentauthority.net/az-tax-sales"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all cursor-pointer whitespace-nowrap bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-map-pin-2-fill text-emerald-400"></i>
                </div>
                AZ Tax Sales
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-external-link-line text-xs"></i>
                </div>
              </a>
              <Link
                to="/tax-foreclosures"
                className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all cursor-pointer whitespace-nowrap bg-amber-500 text-[#0d1421] hover:bg-amber-400"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-map-2-fill"></i>
                </div>
                View All 3 States
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
